import { StopwatchService } from '@/services/stopwatch/stopwatch.service'
import GameService from '@/services/game.service'
import { useStore } from '@/store'
import { computed, ref, watch } from 'vue'
import { getPublicChannel } from '@/ably'
import router from '@/router'
import { routeAfterGame } from '@/use/useStartPage'
import { uuidv4 } from '@/lib/uuid'
import { electActiveMember, periodBounds, shouldInitTimeByPeriod, stopwatchKey } from '@/lib/stopwatch'

const gameUuid = ref(null)
const diffTime = ref(0)
const shotClockDiffTime = ref(0)
const activeStopwatch = ref(null)
const stopwatchId = ref(null)
const timeData = ref({
  time: 0,
  shotClock: 0,
  isRunning: false,
  isShotClockUpdated: false
})
const stopwatchChannel = ref(null)
const isFrameAnimating = ref(false)
const animateFrame = ref(null)
const isLoaded = ref(false)
const startTime = ref(0)
const startShotClockTime = ref(0)
const isEditMode = ref(false)
const gameChannel = ref(null)
const stopwatchType = ref('stopwatch')

const isKeyboardShortcutsEnabled = ref(true)
const timeInputs = ref(['', '', '', ''])

const currentPeriod = ref(null)
const periodTime = ref(null)
const overtimePeriodTime = ref(null)
const numberOfRegularPeriods = ref(0)
const isStopwatchInitialized = ref(false)
// false until the first clock of the current period was found in the timer service (missing record = no time)
const hasStoredTime = ref(false)

// The timer service keeps one clock per game AND period (type + number), see stopwatchKey()
const timerKey = computed(() => stopwatchKey(gameUuid.value, currentPeriod.value))

// A failed attach (the Ably connection is down or suspended) must not stop the stopwatch: the listener is registered
// before the attach, and Ably attaches the channels and enters the presence again when the connection returns.
function ignoreAblyError (promise) {
  return Promise.resolve(promise).catch((error) => {
    console.warn('Ably is not available yet:', error?.message ?? error)
  })
}

export function useStopwatch () {
  const store = useStore()
  const currentClient = computed(() => store.state.currentClient)
  const config = computed(() => store.state.config)

  const isReverse = computed(() => store.state.config?.time_reverse ?? false)
  const isActiveCurrentStopwatch = computed(() => activeStopwatch.value?.clientId === stopwatchId.value)

  const isRunning = computed(() => timeData.value.isRunning)
  const currentTime = computed(() => diffTime.value)
  const currentShotClock = computed(() => timeData.value.shotClock)

  async function updateTime (data) {
    if (!isActiveCurrentStopwatch.value) {
      return
    }

    return StopwatchService.setTime(currentClient.value.uuid, timerKey.value, data)
  }

  function whenConfigLoaded () {
    if (store.state.config) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const unwatch = watch(() => store.state.config, (value) => {
        if (value) {
          unwatch()
          resolve()
        }
      })
    })
  }

  async function getGame () {
    const res = await GameService.stopwatch()

    gameUuid.value = res.data.gameUuid
    currentPeriod.value = res.data.currentPeriod
    periodTime.value = res.data.periodTime
    overtimePeriodTime.value = res.data.overtimePeriodTime
    numberOfRegularPeriods.value = res.data.numberOfRegularPeriods

    return res.data
  }

  async function getTime () {
    return StopwatchService.getTime(currentClient.value.uuid, timerKey.value)
  }

  const bounds = computed(() => periodBounds({
    isReverse: isReverse.value,
    period: currentPeriod.value,
    periodTime: periodTime.value,
    overtimePeriodTime: overtimePeriodTime.value,
    numberOfRegularPeriods: numberOfRegularPeriods.value
  }))

  const periodMaxTimeOnPeriod = computed(() => bounds.value.max)

  const periodMinTimeOnPeriod = computed(() => bounds.value.min)

  const shotClockSeconds = computed(() => {
    return Math.floor(shotClockDiffTime.value / 1000) % 60
  })

  const shotClockMilliSeconds = computed(() => {
    return Math.floor(shotClockDiffTime.value % 1000)
  })

  const formattedShotClockSeconds = computed(() => {
    return zeroPad(shotClockSeconds.value)
  })

  const formattedShotClockMilliSeconds = computed(() => {
    return Math.floor((shotClockDiffTime.value % 1000) / 100).toString()
  })

  const minutes = computed(() => {
    return zeroPad(Math.floor(diffTime.value / 1000 / 60))
  })

  const seconds = computed(() => {
    return zeroPad(Math.floor(diffTime.value / 1000) % 60)
  })

  const milliSeconds = computed(() => {
    return Math.floor(diffTime.value % 1000)
  })

  const formattedMinutes = computed(() => {
    return zeroPad(minutes.value)
  })

  const formattedSeconds = computed(() => {
    return zeroPad(seconds.value)
  })

  const formattedMilliSeconds = computed(() => {
    return Math.floor(milliSeconds.value / 100).toString()
  })

  const currentTimeToShow = computed(() => {
    return formattedMinutes.value + ':' + formattedSeconds.value + '.' + formattedMilliSeconds.value
  })

  const currentShotClockToShow = computed(() => {
    return formattedShotClockSeconds.value + '.' + formattedShotClockMilliSeconds.value
  })

  function zeroPad (value, num) {
    const newNum = typeof num !== 'undefined' ? num : 2
    return value.toString().padStart(newNum, '0')
  }

  function initTimeByPeriod () {
    if (isActiveCurrentStopwatch.value) {
      diffTime.value = bounds.value.start

      stopTimer()
    }
  }

  async function initStopwatch (type = 'stopwatch') {
    stopwatchType.value = type
    if (!stopwatchId.value) {
      stopwatchId.value = uuidv4()
    }
    isLoaded.value = false

    // The sport config decides the direction of the clock and arrives with its own request: the period range of the
    // stored clock must not be computed before it is there.
    await whenConfigLoaded()

    await getGame()

    gameChannel.value = getPublicChannel(`${currentClient.value.uuid}.game.${gameUuid.value}`)

    await ignoreAblyError(gameChannel.value.subscribe((message) => {
      switch (message.name) {
        case 'start':
          // location.reload()
          break
        case 'period':
          currentPeriod.value = message.data.game.currentPeriod
          initTimeByPeriod()
      }
    }))

    const response = await getTime()

    // The timer service answers {} when it has no clock for this period yet: then the clock starts at the period start
    hasStoredTime.value = response.data?.time !== undefined && response.data?.time !== null

    timeData.value.time = response.data?.time ?? 0
    timeData.value.shotClock = response.data?.shotClock ?? 0
    timeData.value.isRunning = response.data?.isRunning ?? false

    diffTime.value = timeData.value.time
    shotClockDiffTime.value = timeData.value.shotClock

    // Only here, once per page load, the stored clock is checked against the period. A presence change later on must
    // never touch the clock (it used to reset it to the value read at page load).
    if (shouldInitTimeByPeriod({
      hasStoredTime: hasStoredTime.value,
      time: timeData.value.time,
      min: periodMinTimeOnPeriod.value,
      max: periodMaxTimeOnPeriod.value
    })) {
      diffTime.value = bounds.value.start
      timeData.value.time = bounds.value.start
      timeData.value.isRunning = false
    }

    hasStoredTime.value = true

    if (timeData.value.isRunning) {
      await startTimer(true)
    } else {
      await stopTimer(true)
    }

    stopwatchChannel.value = getPublicChannel(`${currentClient.value.uuid}.game.${gameUuid.value}.stopwatch`)

    await ignoreAblyError(stopwatchChannel.value.presence.subscribe('enter', (member) => {
      setMember()
    }))

    await ignoreAblyError(stopwatchChannel.value.presence.subscribe('leave', (member) => {
      setMember()
    }))

    await ignoreAblyError(stopwatchChannel.value.subscribe((message) => {
      switch (message.name) {
        case 'eject':
          if (message.data?.clientId === stopwatchId.value) {
            router.push(routeAfterGame(store))
          }

          break
      }
    }))

    await ignoreAblyError(stopwatchChannel.value.presence.enterClient(stopwatchId.value, { type: stopwatchType.value }))

    isLoaded.value = true
  }

  async function setMember () {
    let members

    try {
      members = await stopwatchChannel.value.presence.get()
    } catch (error) {
      // No connection: the next presence change (when it returns) elects again
      console.warn('Ably is not available yet:', error?.message ?? error)
      return
    }

    const active = electActiveMember(members)

    if (!active) {
      return
    }

    await setActiveStopwatch({ clientId: active.clientId, data: { type: active.data?.type } })

    if (active.clientId === stopwatchId.value) {
      await unsubscribeOnStopwatchChannel()
    } else {
      await subscribeOnStopwatchChannel()
    }
  }

  async function toggleTime () {
    if (!isActiveCurrentStopwatch.value || !isKeyboardShortcutsEnabled.value) {
      return
    }

    if (isRunning.value) {
      await stopTimer()
    } else {
      await startTimer()
    }
  }

  const updateTimeListener = async (message) => {
    switch (message.name) {
      case 'update_time':
        if (isActiveCurrentStopwatch.value || !message?.data) {
          return
        }

        // eslint-disable-next-line no-case-declarations
        const shouldRun = message.data.isRunning

        timeData.value = { ...message.data }

        diffTime.value = message.data.time
        shotClockDiffTime.value = message.data.shotClock

        // Always start from the received time: a running local loop keeps its old start anchor and would overwrite
        // an edited time (and the network delay of the previous update) on the next frame.
        await stopTimer(true)

        if (shouldRun) {
          await startTimer(true)
        }

        break
    }
  }
  async function subscribeOnStopwatchChannel () {
    // The same listener must not be registered twice (setMember runs on every presence change)
    await stopwatchChannel.value.unsubscribe(updateTimeListener)
    await ignoreAblyError(stopwatchChannel.value.subscribe(updateTimeListener))
  }

  async function unsubscribeOnStopwatchChannel () {
    await stopwatchChannel.value.unsubscribe(updateTimeListener)
  }

  async function setActiveStopwatch (member) {
    activeStopwatch.value = member

    isEditMode.value = false
  }

  async function ejectTimer () {
    if (stopwatchChannel.value && isActiveCurrentStopwatch.value) {
      await stopwatchChannel.value.publish('eject', activeStopwatch.value)
    }
  }

  async function destroyStopwatch () {
    if (stopwatchChannel.value) {
      await ignoreAblyError(stopwatchChannel.value.presence.unsubscribe())
      await ignoreAblyError(stopwatchChannel.value.unsubscribe())
      await ignoreAblyError(stopwatchChannel.value.detach())
    }
    if (gameChannel.value) {
      await ignoreAblyError(gameChannel.value.unsubscribe())
      await ignoreAblyError(gameChannel.value.detach())
    }
  }

  function toggleEditMode () {
    isEditMode.value = !isEditMode.value
  }

  async function increaseTime (by = 1000) {
    const isRunningNow = isRunning.value

    let time

    if (by === 100) {
      time = Math.floor(diffTime.value / by) * by + by
    } else {
      time = diffTime.value + by
    }

    if (time > periodMaxTimeOnPeriod.value) {
      time = periodMaxTimeOnPeriod.value
    }

    if (diffTime.value === time) {
      return
    }

    if (isRunningNow) {
      await stopTimer(true)
    }

    diffTime.value = time

    if (isRunningNow) {
      await startTimer(true)
    }

    await updateStopwatchTime()
  }

  async function decreaseTime (by = 1000) {
    const isRunningNow = isRunning.value

    let time

    if (by === 100) {
      time = Math.floor(diffTime.value / by) * by - by
    } else {
      time = diffTime.value - by
    }

    if (time < periodMinTimeOnPeriod.value) {
      time = periodMinTimeOnPeriod.value
    }

    if (diffTime.value === time) {
      return
    }

    if (isRunningNow) {
      await stopTimer(true)
    }

    diffTime.value = time

    if (isRunningNow) {
      await startTimer(true)
    }

    await updateStopwatchTime()
  }

  async function increaseShotClockTime (by = 1000) {
    if (isRunning.value) {
      return
    }

    let time

    if (by === 100) {
      time = Math.floor(timeData.value.shotClock / by) * by + by
    } else {
      time = timeData.value.shotClock + by
    }

    if (time > 24000) {
      time = 24000
    }

    if (timeData.value.shotClock === time) {
      return
    }

    timeData.value.shotClock = time

    await updateStopwatchTime()
  }

  async function decreaseShotClockTime (by = 1000) {
    if (isRunning.value) {
      return
    }

    let time

    if (by === 100) {
      time = Math.floor(timeData.value.shotClock / by) * by - by
    } else {
      time = timeData.value.shotClock - by
    }

    if (time < 0) {
      time = 0
    }

    if (timeData.value.shotClock === time) {
      return
    }

    timeData.value.shotClock = time

    await updateStopwatchTime()
  }

  function updateStopwatchTimeEverySecond () {
    if (isRunning.value) {
      const data = { time: diffTime.value, shotClock: shotClockDiffTime.value, isRunning: isRunning.value, isShotClockUpdated: false, periodMax: periodMaxTimeOnPeriod.value }

      updateTime(data)
    }
  }

  function playBuzzer () {
    if (stopwatchChannel.value && isActiveCurrentStopwatch.value) {
      stopwatchChannel.value.publish('buzzer', null)
    }
  }

  async function updateStopwatchTime (isShotClockUpdated = false) {
    if (!stopwatchChannel.value || !isActiveCurrentStopwatch.value) {
      return
    }

    const data = {
      isRunning: timeData.value.isRunning,
      time: diffTime.value,
      shotClock: shotClockDiffTime.value ?? 0,
      isShotClockUpdated,
      periodMax: periodMaxTimeOnPeriod.value
    }

    await stopwatchChannel.value.publish('update_time', data)

    updateTime(data)
  }

  function setSubtractStartTime (time) {
    const newTime = typeof time !== 'undefined' ? time : 0
    startTime.value = Math.floor((isReverse.value ? -performance.now() : performance.now()) - newTime)
  }

  function setSubtractShotClockStartTime (time) {
    const newTime = typeof time !== 'undefined' ? time : 0
    startShotClockTime.value = Math.floor(-performance.now() - newTime)
  }

  async function resetShotClock (seconds) {
    shotClockDiffTime.value = seconds * 1000
    setSubtractShotClockStartTime(shotClockDiffTime.value)
    await updateStopwatchTime(true)
  }

  async function startTimer (isSyncing = false) {
    // A count-up clock standing on the end of the period we know: the next period may have started without us being
    // told (a missed 'period' message), and the loop would stop the clock at once. Ask which period it is.
    if (!isSyncing && !isReverse.value && diffTime.value >= periodMaxTimeOnPeriod.value) {
      try {
        await getGame()
      } catch (e) {
        console.error(e)
      }
    }

    if (isFrameAnimating.value) {
      return
    }

    timeData.value.isRunning = true

    setSubtractStartTime(diffTime.value)
    setSubtractShotClockStartTime(shotClockDiffTime.value)
    await loop()

    if (!isSyncing) {
      await updateStopwatchTime()
    }
  }

  async function loop () {
    if (isReverse.value && (diffTime.value <= 100)) {
      diffTime.value = 0
      await stopTimer()
      return
    } else if (!isReverse.value && (diffTime.value >= periodMaxTimeOnPeriod.value)) {
      diffTime.value = periodMaxTimeOnPeriod.value
      await stopTimer()
      return
    }

    if (shotClockDiffTime.value < 100) {
      shotClockDiffTime.value = 0
    } else {
      shotClockDiffTime.value = Math.max(0, -performance.now() - startShotClockTime.value)
    }

    const nowTime = Math.floor(performance.now())
    diffTime.value = (isReverse.value ? -nowTime : nowTime) - startTime.value

    animateFrame.value = requestAnimationFrame(loop)
    isFrameAnimating.value = true
  }

  async function stopTimer (isSyncing = false) {
    timeData.value.isRunning = false

    if (animateFrame.value) {
      cancelAnimationFrame(animateFrame.value)
      isFrameAnimating.value = false
    }

    if (!isSyncing) {
      await updateStopwatchTime()
    }
  }

  function prepareTimeInputs () {
    const mins = Math.floor(diffTime.value / 1000 / 60).toString().padStart(2, '0')
    const secs = (Math.floor(diffTime.value / 1000) % 60).toString().padStart(2, '0')
    timeInputs.value = [mins[0], mins[1], secs[0], secs[1]]
  }

  if (!isStopwatchInitialized.value) {
    watch(computed(() => Math.floor(diffTime.value / 1000) % 60), () => {
      if (isActiveCurrentStopwatch.value && timeData.value.isRunning && gameUuid.value) {
        const data = { time: diffTime.value, shotClock: shotClockDiffTime.value, isRunning: timeData.value.isRunning, isShotClockUpdated: false, periodMax: periodMaxTimeOnPeriod.value }
        StopwatchService.setTime(currentClient.value?.uuid, timerKey.value, data)
      }
    }, { immediate: false })
    isStopwatchInitialized.value = true
  }

  return {
    periodMaxTimeOnPeriod,
    periodMinTimeOnPeriod,
    currentTimeToShow,
    currentShotClockToShow,
    formattedMinutes,
    formattedSeconds,
    formattedMilliSeconds,
    formattedShotClockSeconds,
    formattedShotClockMilliSeconds,
    minutes,
    seconds,
    milliSeconds,
    shotClockSeconds,
    shotClockMilliSeconds,
    isReverse,
    currentClient,
    config,
    gameUuid,
    currentPeriod,
    periodTime,
    overtimePeriodTime,
    numberOfRegularPeriods,
    timeData,
    currentTime,
    currentShotClock,
    updateTime,
    getTime,
    initStopwatch,
    destroyStopwatch,
    toggleEditMode,
    increaseTime,
    decreaseTime,
    increaseShotClockTime,
    decreaseShotClockTime,
    updateStopwatchTimeEverySecond,
    playBuzzer,
    resetShotClock,
    isKeyboardShortcutsEnabled,
    startTimer,
    loop,
    stopTimer,
    prepareTimeInputs,
    timeInputs,
    isEditMode,
    isRunning,
    isLoaded,
    activeStopwatch,
    toggleTime,
    ejectTimer,
    isActiveCurrentStopwatch,
    updateStopwatchTime,
    diffTime,
    shotClockDiffTime,
    stopwatchId
  }
}
